/**
 * Document-scoped singleton helper for cross-bundle state.
 *
 * SDX bundles each component as its own JS chunk and externalizes
 * a fixed list of runtime modules (`react`, `react-dom`,
 * `@sdx/runtime`, etc.). Anything else - including the wrappers
 * exported via `hook_sdx_aliases` - is inlined into every bundle
 * that imports it. A module-level constant in a shared library
 * file therefore produces N copies at runtime, one per bundle,
 * which breaks any cross-bundle communication that assumes
 * referential identity.
 *
 * `sharedSingleton(key, factory)` collapses those N copies to
 * one document-scoped instance by stashing the value on
 * `globalThis` under a single namespace. The factory runs at
 * most once per document; subsequent calls from any bundle
 * return the same reference.
 *
 * This is the escape hatch for the bundle-duplication problem.
 * Per-render state, per-component state, and pub/sub-shaped
 * communication should use framework hooks, props, or
 * `@sdx/channel` / `@sdx/broadcast` from `sdx_realtime`.
 */

const REGISTRY_KEY = '__sdxShared';

interface SharedGlobal {
  [REGISTRY_KEY]?: { values: Map<string, unknown> };
}

const getRegistry = (): Map<string, unknown> => {
  const target = globalThis as SharedGlobal;
  let registry = target[REGISTRY_KEY];
  if (!registry) {
    registry = { values: new Map() };
    target[REGISTRY_KEY] = registry;
  }
  return registry.values;
};

/**
 * Returns the singleton bound to `key`, creating it on first call.
 *
 * The factory is invoked exactly once per document, regardless of
 * how many bundles call `sharedSingleton(key, factory)`. Callers
 * are responsible for namespacing keys; collisions overwrite, so
 * prefer reverse-DNS strings (`'com.example.feature.registry'`)
 * over short names.
 *
 * @param key
 *   Document-unique identifier for the singleton.
 * @param factory
 *   Lazy initializer. Runs at most once per document.
 *
 * @returns
 *   The shared instance.
 */
export const sharedSingleton = <T>(key: string, factory: () => T): T => {
  const registry = getRegistry();
  if (!registry.has(key)) {
    registry.set(key, factory());
  }
  return registry.get(key) as T;
};

/**
 * Returns TRUE when a singleton has been initialized for `key`.
 *
 * Lets callers detect first-bundle vs. later-bundle initialization
 * without racing the factory.
 */
export const hasSharedSingleton = (key: string): boolean =>
  getRegistry().has(key);
